'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RealtimeCheckIn {
  id: string;
  mood: number;
  urge: number;
  checked_in_at: string;
}

export default function RealtimeCheckInBadge() {
  const { user } = useAuth();
  const [latestCheckIn, setLatestCheckIn] = useState<RealtimeCheckIn | null>(null);
  const [justUpdated, setJustUpdated] = useState(false);
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    // Prevent double-subscription in React Strict Mode
    if (subscribedRef.current) return;
    subscribedRef.current = true;

    let mounted = true;
    const supabase = createClient();
    const channelName = `check_ins_${user.id}`;

    // Fetch latest check-in
    supabase
      .from('check_ins')
      .select('id, mood, urge, checked_in_at')
      .eq('user_id', user.id)
      .order('checked_in_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (mounted && data) setLatestCheckIn(data);
      });

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'check_ins',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (!mounted) return;
          setLatestCheckIn(payload.new as RealtimeCheckIn);
          setJustUpdated(true);
          setTimeout(() => {
            if (mounted) setJustUpdated(false);
          }, 3000);
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      subscribedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [user]);

  if (!latestCheckIn) return null;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 transition-all duration-500 ${justUpdated ? 'scale-105' : ''}`}
      style={{
        background: justUpdated ? 'rgba(34,197,94,0.15)' : 'rgba(0,51,153,0.08)',
        border: `1px solid ${justUpdated ? 'rgba(34,197,94,0.3)' : 'rgba(0,51,153,0.15)'}`,
        color: justUpdated ? '#16a34a' : '#003399',
      }}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${justUpdated ? 'bg-green-500 animate-pulse' : 'bg-primary/60'}`} />
      {justUpdated ? '✓ Synced to cloud' : `Last check-in: Mood ${latestCheckIn.mood}/5`}
    </div>
  );
}
